import { FC } from 'react';

import { FieldArray, FormikContextType, FormikProvider, getIn } from 'formik';
import { Plus, Trophy, X } from 'lucide-react';

import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { ITournamentForm, TOURNAMENT_REWARD_STUB, TournamentReward } from '../form';

import { getRewardPlacesLabel, getRewardValueFieldConfig } from './utils';

import css from './TournamentRewardsFormControl.module.css';

import { TournamentRewardType } from 'v2/constants';

interface TournamentRewardsFormControlProps {
  formik: FormikContextType<ITournamentForm>;
}

export const TournamentRewardsFormControl: FC<TournamentRewardsFormControlProps> = ({ formik }) => {
  const rewardsError = typeof formik.errors.rewards === 'string' ? formik.errors.rewards : null;

  return (
    <div className={css.tournamentRewardsFormControl}>
      <p className={css.tournamentRewardsFormControlTitle}>Rewards *</p>

      {rewardsError && <FormHelperText error>{rewardsError}</FormHelperText>}

      <FormikProvider value={formik}>
        <FieldArray name="rewards">
          {({ push, remove, form }) => {
            const totalPlaces = form.values.rewards.reduce((acc: number, reward: TournamentReward) => {
              const count = Number(reward.count);
              return Number.isFinite(count) && count > 0 ? acc + count : acc;
            }, 0);

            return (
              <div className="form">
                {form.values.rewards.map((reward: TournamentReward, i: number) => {
                  const typeError = getIn(form.errors, `rewards.${i}.type`);
                  const valueError = getIn(form.errors, `rewards.${i}.value`);
                  const countError = getIn(form.errors, `rewards.${i}.count`);

                  const valueFieldConfig = getRewardValueFieldConfig(reward.type);

                  return (
                    <div key={i} className={css.tournamentRewardCard}>
                      <div className={css.tournamentRewardCardTitleContainer}>
                        <p className={css.tournamentRewardCardTitle}>
                          <Trophy width={16} height={16} />
                          {i + 1} Reward
                        </p>

                        <IconButton color="error" onClick={() => remove(i)}>
                          <X width={18} height={18} />
                        </IconButton>
                      </div>

                      <div className={css.tournamentRewardCardFields}>
                        <FormControl size="small" fullWidth required error={Boolean(typeError)}>
                          <InputLabel id={`reward-type-label-${i}`}>Type</InputLabel>
                          <Select
                            labelId={`reward-type-label-${i}`}
                            name={`rewards.${i}.type`}
                            value={reward.type}
                            label="Type"
                            onChange={(event) => {
                              formik.setFieldValue(`rewards.${i}.type`, event.target.value);
                              formik.setFieldValue(`rewards.${i}.value`, '');
                            }}
                          >
                            <MenuItem value={TournamentRewardType.BALANCE}>Balance</MenuItem>
                            <MenuItem value={TournamentRewardType.DEFAULT_CONTEST_TICKET}>
                              Default Contest Ticket
                            </MenuItem>
                            <MenuItem value={TournamentRewardType.CUSTOM}>Custom</MenuItem>
                          </Select>
                          {typeError && <FormHelperText>{typeError}</FormHelperText>}
                        </FormControl>

                        <TextField
                          name={`rewards.${i}.value`}
                          value={reward.value}
                          onChange={form.handleChange}
                          label={valueFieldConfig.label}
                          size="small"
                          type="text"
                          fullWidth
                          required
                          error={Boolean(valueError)}
                          helperText={valueError}
                        />

                        <TextField
                          name={`rewards.${i}.count`}
                          value={reward.count}
                          onChange={form.handleChange}
                          label="Count"
                          size="small"
                          type="number"
                          inputProps={{ min: 1 }}
                          fullWidth
                          required
                          error={Boolean(countError)}
                          helperText={countError}
                        />
                      </div>

                      <FormHelperText>{getRewardPlacesLabel(form.values.rewards, i)}</FormHelperText>
                    </div>
                  );
                })}

                <p className={css.tournamentRewardsFormControlSummary}>Total prize places: {totalPlaces}</p>

                <Button variant="outlined" onClick={() => push(TOURNAMENT_REWARD_STUB)} fullWidth>
                  <Plus width={18} height={18} /> Reward
                </Button>
              </div>
            );
          }}
        </FieldArray>
      </FormikProvider>
    </div>
  );
};
